// src/components/CarList.jsx
import React, { useEffect, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFilters } from "../stores/useFilters";
import { getSuggestions, getVehicles } from "../api/cars";
import FilterSidebar from "../components/FilterSidebar";
import CarCard from "../components/CarCard";
import {
  FunnelIcon,
  ArrowsUpDownIcon,
  XMarkIcon,
  // ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import CompareBar from "../components/CompareBar";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
// import { IoArrowDown, IoArrowUp } from "react-icons/io5";

export default function CarList() {
  const { values: filters, set: setFilter } = useFilters();

  // ─── LOCAL SORT & PAGINATION STATE ─────────────────────────────────
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);

  // ─── MOBILE DRAWERS STATE ────────────────────────────────────────────
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const [showMobileToolbar, setShowMobileToolbar] = useState(false);

  const [selectedCars, setSelectedCars] = useState([]);
  const navigate = useNavigate();

  const drawerRef = useRef(null);

  // ── Search text + dropdown control ───────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  // ── debounced value for searchQuery ───────────────────
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300); // ← wait 300ms after last keystroke
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => setPage(1), [filters, sortBy, sortOrder]);

  // ─── Toggle helper ───────────────────────────────────────────
  const toggleCompare = (car) => {
    console.log("toggle compare", car);
    setSelectedCars((prev) => {
      const exists = prev.find((c) => c.base.id === car.base.id);
      if (exists) {
        // If already selected, just remove it
        return prev.filter((c) => c.base.id !== car.base.id);
      } else {
        if (prev.length >= 2) {
          // ← Show warning toast and do not add a third
          toast.warning("You can only compare two cars at a time.");
          return prev;
        }
        return [...prev, car];
      }
    });
  };

  const clearCompare = () => setSelectedCars([]);

  const {
    data: resp,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["cars", filters, page, limit, sortBy, sortOrder],
    queryFn: () =>
      getVehicles({
        ...filters,
        page,
        limit,
        sortBy,
        sortOrder,
      }),
    keepPreviousData: true,
    staleTime: 60_000,
  });

  // Show all cars, including sold
  const vehicles = resp?.data || [];
  const totalPages = resp?.totalPages || 1;
  const total = resp?.total ?? 0;
  const count = vehicles.length;

  // calculate start/end indexes
  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, start + count - 1);

  const { data: suggestions = [], isFetching: loadingSuggestions } = useQuery({
    queryKey: ["suggestions", debouncedSearch],
    queryFn: () => getSuggestions(debouncedSearch),
    enabled: debouncedSearch.length >= 1,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    function onScroll() {
      // Show toolbar if scrolled down more than 100px, hide if near top
      setShowMobileToolbar(window.scrollY > 100);
    }
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="w-full">
      {/* HERO SECTION */}
      <section className="relative w-full h-[25vh] md:h-[35vh] flex flex-col items-center justify-center text-white overflow-hidden">
        {/* FULL OVERLAY tint instead of gradient */}
        <div
          className="absolute inset-0 z-10"
          style={{
            background:
              "linear-gradient(rgba(31, 41, 84, 1) 0%, rgba(239, 81, 75, 1) 100%)",
          }}
        />

        {/* Search bar container */}
        <div className="relative z-20 w-full px-4 md:px-20 mt-20">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setFilter("q", searchQuery);

              // setFilter({ ...filters, q: searchQuery });
              setPage(1);
            }}
            className="max-w-3xl mx-auto"
          >
            <div className="relative">
              {/* Search icon */}
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary" />

              {/* Input */}
              <input
                type="text"
                name="q"
                placeholder="Search by make or model"
                className="
                w-full
                bg-white bg-opacity-90
                text-gray-800 placeholder-gray-500
                rounded-full
                pl-12 pr-4 py-3
                focus:outline-none focus:ring-2 focus:ring-red-400
                transition
              "
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() =>
                  // small delay so click on suggestion fires
                  setTimeout(() => setShowSuggestions(false), 100)
                }
              />

              {/* Suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute mt-1 w-full bg-white border border-gray-200 rounded shadow-lg z-30 max-h-60 overflow-auto">
                  {suggestions.map((s) => (
                    <li
                      key={s}
                      className="px-4 py-2 text-primary hover:bg-gray-100 cursor-pointer"
                      onMouseDown={() => {
                        // use onMouseDown so we don't lose input focus too early
                        setSearchQuery(s);
                        setFilter("q", s);
                        setShowSuggestions(false);
                        setPage(1);
                      }}
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </form>

          <h2 className="z-20 text-base md:text-lg font-bold text-center text-white">
            All our cars are VAT qualifying
          </h2>
          <p className="md:hidden text-center text-white text-sm mt-1">
            📞 Call us ·{" "}
            <a href="tel:03337723333" className="underline hover:text-gray-200">
              0333 772 3333
            </a>
          </p>
        </div>

        <div className="relative z-20 w-full px-4 md:px-20 mt-4"></div>
      </section>

      <div className="mx-auto px-4 py-32 bg-white pb-80 -mb-60">
        {/* ─── Show the CompareBar whenever ≥1 car is selected ─── */}
        {selectedCars.length > 0 && (
          <CompareBar
            selectedCars={selectedCars}
            onCompareClick={() => {
              if (selectedCars.length < 2) return;
              const [c1, c2] = selectedCars;
              navigate(`/compare?car1=${c1.base.id}&car2=${c2.base.id}`);
            }}
            onClear={clearCompare}
            onRemove={(car) => toggleCompare(car)}
          />
        )}

        {/* ─── Mobile toolbar ─────────────────────────────────────────── */}
        <div
          className={`md:hidden sticky top-0 z-20 bg-white text-primary px-4 py-2 flex justify-between items-center border-b border-secondary-accent transition-transform duration-300 ${
            showMobileToolbar ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          <button
            onClick={() => {
              setMobileFiltersOpen(true);
              setMobileSortOpen(false);
            }}
            className="flex items-center space-x-1 text-primary"
          >
            <FunnelIcon className="h-5 w-5" />
            <span>Filters</span>
          </button>
          <button
            onClick={() => {
              setMobileSortOpen((o) => !o);
              setMobileFiltersOpen(false);
            }}
            className="flex items-center space-x-1 text-on-surface"
          >
            <ArrowsUpDownIcon className="h-5 w-5" />
            <span>Sort</span>
          </button>
        </div>

        {/* ─── Mobile filter drawer ───────────────────────────────────── */}
        <div
          className={`fixed inset-0 z-50 ${
            mobileFiltersOpen ? "visible" : "invisible"
          }`}
        >
          {/* Backdrop */}
          <div
            className={`fixed inset-0 bg-black/50 transition-opacity duration-500 ease-in-out ${
              mobileFiltersOpen
                ? "opacity-100"
                : "opacity-0 pointer-events-none"
            }`}
            onClick={() => setMobileFiltersOpen(false)}
          />

          {/* Sliding drawer */}
          <aside
            ref={drawerRef}
            className={`
            fixed top-0 bottom-0 right-0 inset-y-0 w-[100%] sm:w-[40%] bg-white shadow-lg
            transform transition-transform z-50 rounded-tl-3xl rounded-bl-3xl duration-500 ease-in-out
            ${mobileFiltersOpen ? "translate-x-0" : "translate-x-full"}
          `}
          >
            <div className="p-4 flex justify-between items-center border-b border-secondary">
              <h3 className="text-2xl font-bold text-accent">Filters</h3>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="p-1 rounded-full hover:bg-surface/90"
              >
                <XMarkIcon className="h-6 w-6 text-primary" />
              </button>
            </div>
            <div className="px-4 pt-14 pb-24 overflow-y-auto h-full space-y-8">
              <FilterSidebar />
            </div>
          </aside>
        </div>

        {/* ─── Mobile sort dropdown ───────────────────────────────────── */}
        {mobileSortOpen && (
          <div className="md:hidden sticky top-[3.5rem] z-20 bg-white text-primary px-4 py-2 mb-5">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-2">
                <label className="text-on-surface">Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="py-1 border border-secondary-accent rounded bg-white"
                >
                  <option value="updatedAt">Recently updated</option>
                  <option value="price">Price</option>
                  <option value="year">Year</option>
                </select>
              </div>
              {/* <button
                onClick={() =>
                  setSortOrder((o) => (o === "asc" ? "desc" : "asc"))
                }
                className="px-2 py-1 border border-secondary-accent rounded bg-surface text-on-surface hover:bg-accent hover:text-on-surface transition"
              >
                {sortOrder === "asc" ? "↑" : "↓"}
              </button> */}
            </div>
          </div>
        )}

        {/* Desktop UI  */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* ─── Desktop sidebar ─────────────────────────────────────── */}
          <aside className="hidden md:block md:w-[25%] sticky top-20 max-h-[calc(100vh-5rem)] overflow-y-auto bg-white rounded-md">
            <FilterSidebar />
          </aside>

          {/* ─── Main list ───────────────────────────────────────────── */}
          <section className="w-full md:flex-1">
            {error ? (
              <p className="text-danger text-center">{error.message}</p>
            ) : isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                {/* ─── Desktop controls ───────────────────────────── */}
                <div className="flex justify-between items-center mb-4">
                  {/* 1) Range indicator */}
                  <div className="text-base text-primary font-bold pt-10 md:pt-0">
                    {start} to {end} of {total.toLocaleString()} results
                  </div>

                  {/* <div className="flex items-center space-x-2">
                    <label className="text-primary">Show:</label>
                    <select
                      value={limit}
                      onChange={(e) => {
                        setLimit(+e.target.value);
                        setPage(1);
                      }}
                      className="py-1 border border-primary rounded bg-white text-primary"
                    >
                      {[10, 20, 30, 40].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div> */}

                  <div className="hidden md:flex items-center space-x-2">
                    <label className="text-base font-bold text-primary">
                      Sort by:
                    </label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="py-1 border border-primary rounded bg-white text-primary"
                    >
                      <option value="updatedAt">Recently updated</option>
                      <option value="price">Price</option>
                      <option value="year">Year</option>
                    </select>
                    {/* <button
                      onClick={() =>
                        setSortOrder((o) => (o === "asc" ? "desc" : "asc"))
                      }
                      className="px-1 py-[6px] border border-primary rounded bg-white text-primary transition"
                    >
                      {sortOrder === "asc" ? (
                        <IoArrowUp className="w-5 h-5 text-primary" />
                      ) : (
                        <IoArrowDown className="w-5 h-5 text-primary" />
                      )}
                    </button> */}
                  </div>
                </div>

                {/* ─── Car grid ───────────────────────────────────── */}
                {vehicles.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-5">
                    {vehicles.map((car) => (
                      <CarCard
                        key={car.base.id}
                        car={car}
                        isCompared={
                          !!selectedCars.find((c) => c.base.id === car.base.id)
                        }
                        onCompareToggle={() => toggleCompare(car)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-accent text-center text-2xl font-bold mt-20">
                    No cars found.
                  </p>
                )}

                {/* ─── Pagination ───────────────────────────────── */}

                <div className="flex justify-center items-center mt-10 space-x-3">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 
      ${
        page === 1
          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
          : "bg-white text-primary border border-primary hover:bg-primary hover:text-white shadow-sm hover:shadow-md"
      }`}
                  >
                    ← Prev
                  </button>

                  <span className="text-primary font-semibold bg-primary/10 px-4 py-2 rounded-lg shadow-inner">
                    Page <span className="text-primary">{page}</span> of{" "}
                    {totalPages}
                  </span>

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 
      ${
        page === totalPages
          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
          : "bg-white text-primary border border-primary hover:bg-primary hover:text-white shadow-sm hover:shadow-md"
      }`}
                  >
                    Next →
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
